import { useState } from 'react'
import { PageHeader, Panel, Button, Chip, Field, FormRow, Select, Textarea, EmptyState, ConfirmDialog, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, patch, del } from '../lib/api'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Reply = { id: string; title: string; body: string; stage: string; category_id: string | null; category_name: string | null; active: boolean }
const STAGE: Record<string, string> = { first: 'First reply', follow_up: 'They answered', connect: 'Connect them', any: 'Any time' }

/**
 * The saved replies library (Ezra's package: a place to pick or paste a reply;
 * the library stays internal). Responders pick from it on a lead.
 * {first_name}, {business}, {phone} and {disclosure} are filled in when one is picked.
 */
export default function SavedReplies() {
  const r = useApi<{ replies: Reply[] }>('/api/saved-replies')
  const cats = useApi<{ categories: { id: string; name: string }[] }>('/api/categories')
  const toast = useToast()
  const [f, setF] = useState({ title: '', body: '', stage: 'first', category_id: '' })
  const [removing, setRemoving] = useState<string | null>(null)
  if (r.error) return <ErrorBlock error={r.error} />
  if (!r.data) return <LoadingBlock />
  const add = async () => {
    try { await post('/api/saved-replies', { ...f, category_id: f.category_id || null }); setF({ title: '', body: '', stage: f.stage, category_id: f.category_id }); toast('Saved', 'pos'); r.reload() }
    catch (e) { toast((e as Error).message, 'neg') }
  }
  const toggle = async (x: Reply) => { try { await patch(`/api/saved-replies/${x.id}`, { active: !x.active }); r.reload() } catch (e) { toast((e as Error).message, 'neg') } }
  return (
    <>
      <PageHeader title="Saved replies" subtitle="Replies your responders can pick on a lead. Only your team sees this library." />
      <div className="stack">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Add a reply</h2></div>
          <div className="form-grid">
            <Field label="Title (for your team)" id="sr-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            <FormRow label="When to use it" htmlFor="sr-stage">
              <Select id="sr-stage" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
                {Object.entries(STAGE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </FormRow>
            <FormRow label="Trade" htmlFor="sr-cat">
              <Select id="sr-cat" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
                <option value="">Every trade</option>
                {(cats.data?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormRow>
          </div>
          <FormRow label="The reply" htmlFor="sr-body" help="Use {first_name}, {business}, {phone} and {disclosure} where they go. A reply naming a business must carry {disclosure}: without it, responders cannot copy it. Posted in public: keep it friendly and short.">
            <Textarea id="sr-body" rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          </FormRow>
          <Button onClick={add} disabled={!f.title.trim() || f.body.trim().length < 10}>Save reply</Button>
        </Panel>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">The library</h2><span className="muted">{r.data.replies.filter((x) => x.active).length} in use</span></div>
          {r.data.replies.length === 0 ? <EmptyState title="No saved replies yet" body="Add the replies that work best, and your responders can pick them on any lead." /> : (
            <div className="bill-list">
              {r.data.replies.map((x) => (
                <div key={x.id} className="bill-row" style={{ alignItems: 'start' }}>
                  <div><strong>{x.title}</strong><div className="muted" style={{ fontSize: 13 }}>{x.body}</div></div>
                  <span className="row-tight"><Chip>{STAGE[x.stage]}</Chip>{x.category_name && <Chip>{x.category_name}</Chip>}{!x.active && <Chip tone="warn">Off</Chip>}</span>
                  <span className="row-tight">
                    <Button variant="ghost" onClick={() => toggle(x)}>{x.active ? 'Turn off' : 'Turn on'}</Button>
                    <Button variant="ghost" onClick={() => setRemoving(x.id)}>Delete</Button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
      {removing && <ConfirmDialog title="Delete this reply?" tone="neg" confirmLabel="Delete" body="Responders will no longer see it. Turning it off keeps it for later."
        onConfirm={async () => { try { await del(`/api/saved-replies/${removing}`); setRemoving(null); r.reload() } catch (e) { toast((e as Error).message, 'neg') } }} onCancel={() => setRemoving(null)} />}
    </>
  )
}
